import React, { ReactNode, use } from 'react';
import { Controller, ControllerProps } from 'react-hook-form';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import AcousticConfiguration from 'features/residents/models/configurations/acoustic-configuration';
import AudioConfiguration from 'features/residents/models/configurations/audio-configuration';
import BaseAlarmOnlyScenarioConfiguration from 'features/residents/models/configurations/base-alarm-only-scenario-configuration';
import BaseAlarmScenarioConfiguration from 'features/residents/models/configurations/base-alarm-scenario-configuration';
import BaseSettingScenarioConfiguration from 'features/residents/models/configurations/base-setting-scenario-configuration';
import VideoConfiguration from 'features/residents/models/configurations/video-configuration';
import OrganisationUnitScenario from 'features/residents/models/organisation-unit-scenario';
import OrganisationUnitScenariosResponse from 'features/residents/models/organisation-unit-scenario-response';
import OrganizationUnitScenarioIcon from 'features/residents/resident-settings/components/organization-unit-scenario-icon/organization-unit-scenario-icon';
import useRowConfigurationAccordion from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/row-configuration-accordion/hooks';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

import './row-configuration-accordion.scss';

import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';

type Props = {
  index: number;
  children: ReactNode;
  scenarioRowTitle: string;
  organizationUnitScenario: OrganisationUnitScenario;
  subtitlePrefix: string;
  configuration:
    | BaseAlarmScenarioConfiguration
    | BaseAlarmOnlyScenarioConfiguration
    | BaseSettingScenarioConfiguration
    | VideoConfiguration
    | AudioConfiguration
    | AcousticConfiguration
    | WanderingDetectionConfiguration;
  whenActiveAlarm?: boolean;
  isAlarmActive?: boolean;
  showAlarmInfo?: boolean;
};

const RowConfigurationAccordion = ({
  index,
  children,
  scenarioRowTitle,
  showAlarmInfo = false,
  organizationUnitScenario,
  subtitlePrefix,
  configuration,
  isAlarmActive,
}: Readonly<Props>): React.JSX.Element => {
  const alarmTimeOut = (configuration as BaseAlarmScenarioConfiguration)?.alarmTimeOut ?? null;
  const isStatusActive = (configuration as BaseAlarmScenarioConfiguration)?.isStatusActive ?? undefined;
  const temporaryAcousticConfiguration =
    (configuration as AcousticConfiguration)?.temporaryConfigurationEnabled &&
    (configuration as AcousticConfiguration)?.temporaryConfiguration
      ? ((configuration as AcousticConfiguration).temporaryConfiguration ?? undefined)
      : undefined;

  const {
    expandScenario,
    toggleExpandAndActivateScenario: toggleExpendAndActivateScenario,
    toggleExpandScenario: toggleExpendScenario,
    renderScenarioRowSubTitleTimeFrame,
    scenarioRowSubTitleAlarmTime,
    scenarioRowTitleAlarmIndicator,
    getAccordionSummeryClassNames,
  } = useRowConfigurationAccordion({
    index,
    subtitlePrefix,
    organizationUnitScenarioIsActive: organizationUnitScenario.isActive,
    temporaryAcousticConfiguration,
    showAlarmInfo,
    alarmTimeOut,
    isAlarmActive,
    isStatusActive,
    configuration,
  });

  const {
    detailEditState: { viewingMode, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);

  const renderControllerSwitch: ControllerProps<OrganisationUnitScenariosResponse>['render'] = ({ field }) => (
    <Switch
      className={'switch'}
      type="checkbox"
      disabled={viewingMode === 'viewing'}
      checked={!!field.value}
      onChange={(_, checked) => toggleExpendAndActivateScenario(checked)}
    />
  );

  return (
    <Accordion expanded={expandScenario} disableGutters>
      <AccordionSummary
        sx={{ padding: 0, margin: '8px 0 0 0', '.MuiAccordionSummary-content': { margin: '0' }, flexDirection: 'row-reverse' }}
        expandIcon={<ExpandMore onClick={toggleExpendScenario} />}
        aria-label="Expand"
        aria-controls="additional-actions1-content"
      >
        <div className={getAccordionSummeryClassNames}>
          <Controller
            name={`organisationUnitScenarios.${index}.isActive`}
            control={residentSettingEditForm.control}
            render={renderControllerSwitch}
          />
          <OrganizationUnitScenarioIcon isActive={organizationUnitScenario.isActive} scenarioType={organizationUnitScenario.scenarioType} />
          <Grid
            className="configuration-row-column"
            onClick={toggleExpendScenario}
            onKeyDown={(e) => e.key === 'Enter' && toggleExpendScenario()}
          >
            <div className="configuration-row-in-column">
              <Typography variant="h5" className="scenario-title">
                {scenarioRowTitle}
              </Typography>
              <Typography variant="body2" className="grey-when-inactive">
                {scenarioRowTitleAlarmIndicator}
              </Typography>
            </div>
            {organizationUnitScenario && (
              <div className="configuration-row-in-column grey-when-inactive">
                {renderScenarioRowSubTitleTimeFrame()}

                {showAlarmInfo && isAlarmActive && <Typography variant="body2">{scenarioRowSubTitleAlarmTime}</Typography>}
              </div>
            )}
          </Grid>
        </div>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '0 0 8px 0', margin: 0 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default RowConfigurationAccordion;
